import { yupResolver } from '@hookform/resolvers/yup';
import { BankAccountType } from '@zorro/clients';
import {
  showSuccessNotification,
  useForm,
  useIdempotencyKey,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';

import { getFormStatusesConfig } from '../AddEmployerFlowUtils';
import {
  PaymentAccountDetailsFormFields,
  paymentAccountDetailsSchema,
} from './PaymentAccountDetailsSchema';

type UsePaymentAccountDetailsFormParams = {
  employerId: string;
};

export const usePaymentAccountDetailsForm = ({
  employerId,
}: UsePaymentAccountDetailsFormParams) => {
  const form = useForm<PaymentAccountDetailsFormFields>({
    mode: 'all',
    resolver: yupResolver(paymentAccountDetailsSchema),
    defaultValues: {
      bankAccountName: '',
      bankAccountNumber: '',
      routingNumber: '',
      bankAccountType: BankAccountType.CHECKING,
      kybConfirmed: false,
    },
  });

  const idempotencyKey = useIdempotencyKey();

  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: employerId && [employerId],
  });

  const { mutate: mutateActivateContacts } = useMonolithMutation({
    method: 'employersControllerActivateContacts',
    shouldInvalidateQueries: false,
  });

  const { mutate: mutateEmployer } = useMonolithMutation({
    method: 'employersControllerUpdate',
    shouldShowLoadingOverlay: false,
  });

  const { mutate: mutateBankAccount } = useMonolithMutation({
    method: 'paymentsControllerLinkEmployerAccountToClientOrganization',
    shouldShowLoadingOverlay: false,
  });

  const { successMessage, nextSignupStatus, submitLabel, navigateTo } =
    getFormStatusesConfig(employer?.signupStatus) || {};

  const onSubmit = async (): Promise<boolean> => {
    if (!employer) {
      return false;
    }

    const isValid = await form.trigger();

    if (!isValid) {
      return false;
    }

    const data = form.getValues();

    try {
      await mutateBankAccount([
        idempotencyKey.get(),
        {
          employerId,
          accountName: data.bankAccountName,
          accountNumber: data.bankAccountNumber,
          routingNumber: data.routingNumber,
          bankAccountType: data.bankAccountType,
        },
      ]);

      await mutateActivateContacts([employerId]);

      await mutateEmployer([
        employerId,
        {
          zorroPartnerId: employer?.zorroPartnerId ?? null,
          signupStatus: nextSignupStatus,
        },
        idempotencyKey.get(),
      ]);

      showSuccessNotification({ message: successMessage });

      return true;
    } catch {
      /* Empty */
      return false;
    }
  };

  return {
    form,
    onSubmit,
    isSubmitting: form.formState.isSubmitting,
    submitLabel,
    navigateTo,
  };
};
